// =========================
// src/themeEditor/utils.js
// =========================
/**
 * 工具函数合集
 * 中文：保持纯函数、无副作用，便于测试与复用；
 * EN: Keep utilities pure and side-effect free for easy testing & reuse.
 */

// ---------- 基础工具 ----------

// camelCase -> "Title Case"
export function prettyLabel(key) {
  // 中文：空值直接返回空字符串，避免 UI 渲染 undefined
  if (!key) return "";
  return String(key)
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^\w/, (c) => c.toUpperCase());
}

// #rgb -> #rrggbb；非法输入返回空字符串
export function normalizeHex(hex) {
  // 中文：兼容 "ffffff" / "#fff" / "#ffffff" 三种形式，非法输入直接丢弃
  if (!hex || typeof hex !== "string") return "";
  let v = hex.trim();
  if (!v.startsWith("#")) v = `#${v}`;
  const re = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;
  if (!re.test(v)) return "";
  if (v.length === 4) {
    const r = v[1], g = v[2], b = v[3];
    v = `#${r}${r}${g}${g}${b}${b}`;
  }
  return v.toLowerCase();
}

// 简单 HEX 合法性校验（#rrggbb）
export function isValidHex(hex) {
  return /^#[0-9a-f]{6}$/i.test(hex || "");
}

// 确保某个颜色存在于 palette，若无则追加；返回 { colors, index }
export function ensureInPalette(colors, hex) {
  const H = normalizeHex(hex || "");
  if (!H) return { colors, index: undefined };
  let idx = colors.indexOf(H);
  if (idx === -1) {
    const next = [...colors, H];
    idx = next.length - 1;
    return { colors: next, index: idx };
  }
  return { colors, index: idx };
}

// 根据关键词对配置键分组（用于 UI 折叠渲染）
export function categorize(keys) {
  const groups = { Home: [], Services: [], Contact: [], Bottom: [], Top: [], Others: [] };
  (keys || []).forEach((k) => {
    const name = String(k || "").toLowerCase();
    if (name.includes("home")) groups.Home.push(k);
    else if (name.includes("services")) groups.Services.push(k);
    else if (name.includes("contact")) groups.Contact.push(k);
    else if (name.includes("bottom")) groups.Bottom.push(k);
    else if (name.includes("top")) groups.Top.push(k);
    else groups.Others.push(k);
  });
  return groups;
}

// 判断颜色是否偏深（用于自动选择副色等场景）
export function isDark(hex) {
  const h = normalizeHex(hex).replace("#", "");
  if (!/^[0-9a-f]{6}$/i.test(h)) return false;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  // YIQ: 0~255，阈值 186（经验值）
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq < 186;
}

// ---------- 设计常量 ----------

// 灰色 / 黑色 / 白色（注意：和产品约定保持一致）
export const BLACK = "#000000";
export const WHITE = "#ffffff";
export const DARK_GREY = "#374151";   // dark grey
export const LIGHT_GREY = "#e5e7eb";  // light grey
export const GREY = "#808080";        // 中性灰（用于映射，不强行写入 palette）

// 固定色：黑/白/灰（light/dark）——仅在 Dual 模式“不可删除”的业务规则里用
// EN: Only enforced as non-deletable in Dual mode (opt2); in Multiple mode these may be removed.
export const FIXED_COLOR_HEX = {
  black:     BLACK,
  white:     WHITE,
  grey:      GREY,
  lightGrey: LIGHT_GREY,
  darkGrey:  DARK_GREY,
};

// 检查一个 hex 是否属于“固定色集合”
export function isFixedColorHex(hex) {
  const h = normalizeHex(hex);
  return (
    h === FIXED_COLOR_HEX.black ||
    h === FIXED_COLOR_HEX.white ||
    h === FIXED_COLOR_HEX.grey ||
    h === FIXED_COLOR_HEX.lightGrey ||
    h === FIXED_COLOR_HEX.darkGrey
  );
}

// ---------- Dual 模式映射 ----------
// 约定：当编辑/保存为 Dual（opt2）时，这些字段将按 token 映射到主/副色或固定灰阶色；
// Multiple（opt3）时，不使用该映射，“改什么存什么”。
export const DUAL_MAPPING = {
  topBannerBackground: "primaryColor",
  topIcons: "secondaryColor",
  bottomText: "dark grey",
  bottomTextSelected: "black",
  bottomBackground: "light grey",
  contactNameIcons: "primaryColor",
  contactDescriptions: "dark grey",
  contactBackground: "secondaryColor",
  homeHeader: "primaryColor",
  homeContent: "dark grey",
  homeQuickLinksEntries: "primaryColor",
  homeBackground: "secondaryColor",
  servicesHeader: "primaryColor",
  servicesContent: "dark grey",
  servicesEntries: "primaryColor",
  servicesBackground: "secondaryColor",
  servicesCardsBackground: "secondaryColor",
};

// 将“文字 token”（black/white/grey/light grey/dark grey/primaryColor/secondaryColor）转 HEX
export function dualLiteralToHex(lit) {
  const k = String(lit || "").toLowerCase();
  if (k === "black") return FIXED_COLOR_HEX.black;
  if (k === "white") return FIXED_COLOR_HEX.white;
  if (k === "grey") return FIXED_COLOR_HEX.grey;
  if (k === "light grey" || k === "light gray") return FIXED_COLOR_HEX.lightGrey;
  if (k === "dark grey" || k === "dark gray") return FIXED_COLOR_HEX.darkGrey;
  return ""; // unknown → caller should handle
}

// 将 token 映射到最终 HEX（包含 primary/secondary）
export function tokenToHex(token, primaryHex, secondaryHex) {
  const t = String(token || "").toLowerCase();
  if (t === "primarycolor" || t === "primary") return normalizeHex(primaryHex);
  if (t === "secondarycolor" || t === "secondary") return normalizeHex(secondaryHex);
  const fixed = dualLiteralToHex(t);
  return fixed || normalizeHex(primaryHex); // 默认回退 primary
}

// primary 模式：按映射补全 configuration（避免空值）
export function ensureFullConfigurationWithMapping(
  partialCfg,
  primaryFallback = "#2003fc",
  secondaryFallback = WHITE
) {
  const cfg = { ...(partialCfg || {}) };
  const primaryHex = normalizeHex(cfg.primaryColor || primaryFallback || "#2003fc");
  const secondaryHex = normalizeHex(cfg.secondaryColor || secondaryFallback || WHITE);
  cfg.primaryColor = primaryHex;
  cfg.secondaryColor = secondaryHex;

  CONFIG_KEYS.forEach((k) => {
    if (DUAL_MAPPING[k]) {
      cfg[k] = tokenToHex(DUAL_MAPPING[k], primaryHex, secondaryHex);
    } else {
      // 背景类用 secondary，其它用 primary
      if (cfg[k]) {
        cfg[k] = normalizeHex(cfg[k]);
      } else {
        const isBackground = /background$/i.test(k) || k === "topBannerBackground";
        cfg[k] = isBackground ? secondaryHex : primaryHex;
      }
    }
  });

  return cfg;
}

// ---------- 验证（Validation） ----------
// 所有校验函数仅返回错误数组，不抛异常；由上层统一展示

// 校验调色盘（长度、格式、重复）
export function validatePalette(palette, { max = 10, requireAtLeast = 1 } = {}) {
  const errors = [];
  if (!Array.isArray(palette)) return ["Palette is not an array."];
  const cleaned = palette.map(normalizeHex).filter(Boolean);
  if (cleaned.length < requireAtLeast) errors.push(`Palette requires at least ${requireAtLeast} color(s).`);
  if (cleaned.length > max) errors.push(`Palette exceeds max size ${max}.`);
  cleaned.forEach((h, i) => {
    if (!/^#[0-9a-f]{6}$/i.test(h)) errors.push(`Invalid HEX at index ${i}: ${palette[i]}`);
  });
  // 重复色：视为错误（阻止保存）
  const seen = new Set();
  const dup = new Set();
  cleaned.forEach((h) => (seen.has(h) ? dup.add(h) : seen.add(h)));
  if (dup.size) errors.push(`Duplicate colors detected: ${[...dup].join(", ")}.`);
  return errors;
}

// 校验索引映射（cfgIndex）是否越界
export function validateCfgIndex(cfgIndex, paletteLength) {
  const errors = [];
  Object.entries(cfgIndex || {}).forEach(([k, v]) => {
    if (typeof v === "number" && (v < 0 || v >= paletteLength)) {
      errors.push(`Index out of range for "${k}": ${v}`);
    }
  });
  return errors;
}

// 校验 “主/副色” 必填逻辑（当需要时）
export function validatePrimarySecondary({ selectionMode, hasSpecialColor, cfgIndex, originalConf }) {
  const errors = [];
  // 若 DB 有 legacy color，保存时会迁移为 primary/secondary，此时无需选择
  if (hasSpecialColor) return errors;
  if (selectionMode === "opt2") {
    const pDb = normalizeHex(originalConf?.primaryColor || "");
    const sDb = normalizeHex(originalConf?.secondaryColor || "");
    const pSel = cfgIndex?.primaryColor;
    const sSel = cfgIndex?.secondaryColor;
    if (!pDb && typeof pSel !== "number") errors.push("Primary color is required in Dual mode.");
    if (!sDb && typeof sSel !== "number") errors.push("Secondary color is required in Dual mode.");
  }
  return errors;
}

// 总校验（聚合业务规则）
export function validateAll({ palette, MAX_COLORS, cfgIndex, selectionMode, hasSpecialColor, originalConf }) {
  const errors = [];
  errors.push(...validatePalette(palette, { max: MAX_COLORS, requireAtLeast: 1 }));
  errors.push(...validateCfgIndex(cfgIndex, (palette || []).length));
  errors.push(...validatePrimarySecondary({ selectionMode, hasSpecialColor, cfgIndex, originalConf }));
  return errors;
}

// 首次创建 venue/theme：按映射一次性补齐 configuration
export function buildThemeFromBaseColor(baseColor) {
  const primary = normalizeHex(baseColor) || "#2003fc";
  const secondary = WHITE; // 也可以按 isDark(primary) 决定白/深灰：isDark(primary) ? WHITE : DARK_GREY

  // 1) 用映射生成完整配置（所有 CONFIG_KEYS 都有值）
  const configuration = ensureFullConfigurationWithMapping(
    {
      color: primary,           // legacy 字段：首存时可带上；服务器落库后你也可以选择清空它
      primaryColor: primary,
      secondaryColor: secondary,
    },
    primary,
    secondary
  );

  // 2) palette（primary 模式规范）：只保留 主色 + 黑/白/深灰/浅灰 共 5 色
  const colors = Array.from(
    new Set(
      [primary, BLACK, WHITE, DARK_GREY, LIGHT_GREY]
        .map(normalizeHex)
        .filter(Boolean)
    )
  );

  return { colors, configuration };
}

// （可选）欧氏距离工具 —— 当前业务未使用，但保留以备后续需求
const hexToRgb = (hex) => {
  const h = normalizeHex(hex).replace(/^#/, "");
  if (!/^[0-9a-f]{6}$/i.test(h)) return [0, 0, 0];
  const bigint = parseInt(h, 16);
  return [(bigint >> 16) & 0xff, (bigint >> 8) & 0xff, bigint & 0xff];
};
export const getColorDistance = (hex1, hex2) => {
  const [r1, g1, b1] = hexToRgb(hex1);
  const [r2, g2, b2] = hexToRgb(hex2);
  return Math.sqrt((r1 - r2) ** 2 + (g1 - g2) ** 2 + (b1 - b2) ** 2);
};

function rgbToHsl(r, g, b) {
  const rn = r / 255, gn = g / 255, bn = b / 255;
  const max = Math.max(rn, gn, bn), min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  const d = max - min;
  if (d === 0) return [0, 0, l];
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;
  switch (max) {
    case rn: h = (gn - bn) / d + (gn < bn ? 6 : 0); break;
    case gn: h = (bn - rn) / d + 2; break;
    default:  h = (rn - gn) / d + 4; break;
  }
  return [h / 6 * 360, s, l];
}

export function describeColor(hex) {
  const normalized = normalizeHex(hex);
  if (!normalized) return 'Unassigned';
  if (normalized === '#ffffff') return 'Soft White';
  if (normalized === '#000000') return 'Absolute Black';

  const [r, g, b] = hexToRgb(normalized);
  const [h, s, l] = rgbToHsl(r, g, b);
  const sat = s * 100, lit = l * 100;

  if (sat < 8) {
    if (lit > 92) return 'Mist White';
    if (lit > 78) return 'Silver Mist';
    if (lit > 60) return 'Stone Grey';
    if (lit > 36) return 'Slate Grey';
    return 'Charcoal';
  }

  const tone = lit > 82 ? 'Soft' : lit < 34 ? 'Deep' : sat > 72 ? 'Vivid' : 'Balanced';

  if (h < 15 || h >= 345) return `${tone} Red`;
  if (h < 40)  return `${tone} Amber`;
  if (h < 58)  return `${tone} Gold`;
  if (h < 80)  return `${tone} Lime`;
  if (h < 150) return `${tone} Green`;
  if (h < 185) return `${tone} Teal`;
  if (h < 220) return `${tone} Blue`;
  if (h < 255) return `${tone} Indigo`;
  if (h < 290) return `${tone} Violet`;
  if (h < 330) return `${tone} Rose`;
  return `${tone} Coral`;
}

// ---------- 配置键清单（与后端字段保持一致） ----------
export const CONFIG_KEYS = [
  "topBannerBackground",
  "topIcons",
  "bottomText",
  "bottomTextSelected",
  "bottomBackground",
  "contactNameIcons",
  "contactDescriptions",
  "contactBackground",
  "homeHeader",
  "homeContent",
  "homeQuickLinksEntries",
  "homeBackground",
  "servicesHeader",
  "servicesContent",
  "servicesEntries",
  "servicesBackground",
  "servicesCardsBackground",
  "color",
  "primaryColor",
  "secondaryColor",
];

// 1) 严格对齐：只保留 CONFIG_KEYS，缺的补成空串
export function ensureExactConfigKeys(config) {
  const out = {};
  (CONFIG_KEYS || []).forEach((k) => { out[k] = (config && typeof config[k] === "string") ? config[k] : ""; });
  return out;
}

// 2) 字段别名（前端显示用；保存仍用后端 key）
export const FIELD_LABELS = {
  primaryColor: "Primary Colour",
  secondaryColor: "Secondary Colour",
  color: "Legacy Colour",
  topBannerBackground: "Top Banner Background",
  topIcons: "Top Icons",
  bottomText: "Bottom Text",
  bottomTextSelected: "Bottom Text Selected",
  bottomBackground: "Bottom Background",
  contactNameIcons: "Contact Name & Icons",
  contactDescriptions: "Contact Descriptions",
  contactBackground: "Contact Background",
  homeHeader: "Home Header",
  homeContent: "Home Content",
  homeQuickLinksEntries: "Home Quick Links Entries",
  homeBackground: "Home Background",
  servicesHeader: "Services Header",
  servicesContent: "Services Content",
  servicesEntries: "Services Entries",
  servicesBackground: "Services Background",
  servicesCardsBackground: "Services Cards Background",
};

export function getFieldLabel(key) {
  return FIELD_LABELS[key] || prettyLabel(key);
}